import Keycloak from 'keycloak-js/lib/keycloak';
import { fetchUsersModules } from './authentication';
import { clearAuthenticationStorage } from '../util/localStorageUtils';
import {
  postLoginSuccess,
  postLoginFail,
  fetchUsersModulesSuccess,
  setCurrentProductLogin
} from '../redux/actions/authentication';
import { fetchResources } from './authentication';

import { filterRole, sortProductsOptions } from './util';

import { get } from '../util/lodashlike';
// Only construct Keycloak when keycloak-url is configured (set by config.js).
// In hybrid/gladiator mode, fe-config-noauth.js clears keycloak-* keys,
// so this is null and all _kc accessors use localStorage fallbacks.
const _keycloakUrl = localStorage.getItem('keycloak-url');
const _kc = _keycloakUrl
  ? new Keycloak({
      realm: localStorage.getItem('keycloak-realm'),
      url: _keycloakUrl,
      clientId: localStorage.getItem('keycloak-client-id')
    })
  : null;
const CM_APPLICATION_ID = 7;

const POST_LOGOUT_REDIRECT_URI = window.location.origin + '/';

const handleAuthenticationFailure = (errorMessage, store) => {
  console.error(errorMessage);
  store.dispatch(postLoginFail({ message: errorMessage }));
  clearAuthenticationStorage();
  if (_kc) {
    _kc.logout({ redirectUri: window.location.origin + '/login' });
  }
};

/**
 * Initializes Keycloak instance and calls the provided callback function if successfully authenticated.
 *
 * @param onAuthenticatedCallback
 */
const initKeycloak = (onAuthenticatedCallback, store) => {
  // In hybrid/gladiator mode (no Keycloak URL), skip SSO — the login page handles auth
  if (!_kc) {
    console.log('Keycloak not configured — using hybrid login page');
    (async () => {
      onAuthenticatedCallback();
    })();
    return;
  }

  _kc
    .init({
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256'
    })
    .then(async (authenticated) => {
      if (!authenticated) {
        console.log('user is not authenticated..!');
        store.dispatch(postLoginFail('user is not authenticated'));
        clearAuthenticationStorage();
        doLogin();
      } else {
        localStorage.setItem('id_token', _kc.token);
        localStorage.setItem('notification-status', true);
        localStorage.setItem('userName', _kc.tokenParsed?.preferred_username);
        const state = store.getState();

        let selectedProduct = get(
          state,
          'authentication.currentProductLogin',
          null
        );

        const usersModulesResponse = await fetchUsersModules();

        if (usersModulesResponse.status === 200 && usersModulesResponse.data) {
          const modules = sortProductsOptions(usersModulesResponse.data || []);
          const usersModules = filterRole(modules);

          // Check if user has any valid roles after filtering
          if (!usersModules || usersModules.length === 0) {
            handleAuthenticationFailure(
              'Access Denied: No valid roles assigned to your account. Please contact your administrator.',
              store
            );
            return;
          }

          const roleCode = localStorage.getItem('roleCode');
          store.dispatch(fetchUsersModulesSuccess(usersModules));
          selectedProduct =
            usersModules.find((m) => m.roleCode === roleCode) ||
            usersModules.find(({ isDefault }) => isDefault) ||
            usersModules[0];
          store.dispatch(setCurrentProductLogin(selectedProduct));
        }

        console.log(selectedProduct, 'selectedProduct');

        const resourceResponse = await fetchResources(
          selectedProduct ? selectedProduct.applicationId : CM_APPLICATION_ID,
          selectedProduct ? selectedProduct.roleCode : null
        );

        if (resourceResponse.status === 200) {
          const resourceDataList = resourceResponse.data.policies;
          store.dispatch(postLoginSuccess(resourceDataList));
        }
      }
      onAuthenticatedCallback();
    })
    .catch((error) => {
      console.error('UserService: Keycloak init failed:', error);
      clearAuthenticationStorage();
      onAuthenticatedCallback();
    });
};

const doLogin = () => {
  if (!_kc) return;
  console.log('UserService: Initiating login...');
  _kc.login();
};

const doLogout = () => {
  if (!_kc) {
    clearAuthenticationStorage();
    window.location.href = '/';
    return;
  }
  console.log(
    'UserService: Keyclaok Post Logout Redirect URI:',
    POST_LOGOUT_REDIRECT_URI
  );
  /* FIN-13986 — NO SYNCHRONOUS CLEAR AFTER THIS CALL. `_kc.logout()` only STARTS
     a navigation; this document keeps running. Wiping `id_token` on the next
     line made PrivateRoute's auth check fail while the redirect was still in
     flight, and its `navigateTo` is a full-page `window.location.replace`, so a
     second navigation raced the first — which is the 404 flash QA reported.

     Storage is cleared on the way back in instead: `initKeycloak` runs
     `check-sso`, finds no session, and clears it there. */
  _kc.logout({ redirectUri: POST_LOGOUT_REDIRECT_URI });
};

const getToken = () => (_kc ? _kc.token : localStorage.getItem('id_token'));

const getTokenParsed = () => (_kc ? _kc.tokenParsed : null);

const isAuthenticated = () =>
  _kc ? _kc.authenticated : !!localStorage.getItem('id_token');

const isLoggedIn = () =>
  _kc ? !!_kc.token : !!localStorage.getItem('id_token');

const updateToken = async () => {
  if (!_kc) return localStorage.getItem('id_token');
  try {
    const refreshed = await _kc.updateToken(30);
    if (refreshed) {
      localStorage.setItem('id_token', _kc.token);
    }
    return _kc.token;
  } catch (error) {
    console.error('Token refresh failed:', error);
    // Store error message before clearing storage
    localStorage.setItem(
      'auth_error_message',
      'Session expired. Please log in again.'
    );
    clearAuthenticationStorage(true);
    // Only redirect if not already on login page
    if (!window.location.pathname.includes('/login')) {
      _kc.logout({ redirectUri: window.location.origin + '/login' });
    }
    throw error; // Let the caller handle the error
  }
};

const getUsername = () =>
  _kc ? _kc.tokenParsed?.preferred_username : 'mock-user';

const getUserRoles = () => {
  if (_kc?.tokenParsed?.realm_access?.roles) {
    return _kc.tokenParsed.realm_access.roles;
  }
  return [];
};

const UserService = {
  _kc,
  initKeycloak,
  doLogin,
  doLogout,
  isAuthenticated,
  isLoggedIn,
  getToken,
  getTokenParsed,
  updateToken,
  getUsername,
  getUserRoles
};

export default UserService;
